import React, { useState } from 'react';
import { Code2, Users, Clock, ArrowRight, Shield, Cloud, GitBranch, Cpu, CheckCircle2, Rocket, Server, Brain, DollarSign, Briefcase } from 'lucide-react';
import ContactModal from '../components/ContactModal';

const DaaS = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');

  const handleGetStarted = (planName: string) => {
    setSelectedPlan(planName);
    setIsModalOpen(true);
  };

  const features = [
    {
      icon: <Users className="w-8 h-8 text-yellow-400" />,
      title: "Dedicated Development Team",
      description: "Get instant access to a skilled team of developers, designers, and project managers without the overhead of hiring."
    },
    {
      icon: <Clock className="w-8 h-8 text-yellow-400" />,
      title: "Flexible Scaling",
      description: "Scale your development team up or down based on project needs, ensuring optimal resource allocation."
    },
    {
      icon: <Shield className="w-8 h-8 text-yellow-400" />,
      title: "Quality Assurance",
      description: "Rigorous testing and quality control processes ensure delivery of robust, production-ready code."
    },
    {
      icon: <Cloud className="w-8 h-8 text-yellow-400" />,
      title: "Cloud-Native Development",
      description: "Modern cloud-first approach ensuring scalability, reliability, and optimal performance."
    }
  ];

  const benefits = [
    {
      title: "Cost Efficiency",
      points: [
        "No recruitment or training costs",
        "Pay only for the resources you need",
        "Reduced infrastructure expenses",
        "Flexible engagement models"
      ]
    },
    {
      title: "Time to Market",
      points: [
        "Immediate team availability",
        "Parallel development tracks",
        "Rapid prototyping",
        "Continuous delivery"
      ]
    },
    {
      title: "Technical Excellence",
      points: [
        "Best practices implementation",
        "Latest technology stack",
        "Security by design",
        "Scalable architecture"
      ]
    }
  ];

  const process = [
    {
      icon: <Brain className="w-8 h-8 text-yellow-400" />,
      title: "Discovery",
      description: "We analyze your needs and create a tailored development strategy."
    },
    {
      icon: <Users className="w-8 h-8 text-yellow-400" />,
      title: "Team Assembly",
      description: "We assign the perfect mix of specialists for your project."
    },
    {
      icon: <GitBranch className="w-8 h-8 text-yellow-400" />,
      title: "Development",
      description: "Agile development with regular updates and demonstrations."
    },
    {
      icon: <Rocket className="w-8 h-8 text-yellow-400" />,
      title: "Delivery & Support",
      description: "Continuous deployment and ongoing maintenance support."
    }
  ];

  const pricingPlans = [
    {
      name: "Builder Base",
      price: "$3,200",
      bestFor: "Light dev support for small builds or steady maintenance",
      description: "Whether you're starting simple or maintaining something stable, this plan gives you a reliable dev team without the overhead.",
      features: [
        "Bug fixes and ongoing improvements",
        "Light new feature development or UI tweaks",
        "Project manager included",
        "Async communication",
        "Email-based support"
      ],
      recommendedFor: "Ideal for early-stage platforms, internal tools, or keeping smaller projects healthy."
    },
    {
      name: "Growth Partner",
      price: "$5,800",
      bestFor: "Reliable dev support for active projects or new builds",
      description: "Launch new features, build your MVP, or evolve your existing platform — all with a PM guiding the process.",
      features: [
        "Feature development or greenfield app builds",
        "Weekly planning/check-in calls",
        "Backlog grooming & management",
        "Prioritized, fast delivery",
        "Priority support & direct PM access"
      ],
      recommendedFor: "Great for product teams building from scratch or improving something already in market."
    },
    {
      name: "Velocity",
      price: "$9,600",
      bestFor: "High-speed delivery across multiple workstreams",
      description: "Need to move fast across several areas? This tier supports both new builds and mature platforms juggling big priorities.",
      features: [
        "Multi-stream development",
        "Dedicated PM + 1–2 developers",
        "Slack/Teams access",
        "Bi-weekly roadmap and strategy calls",
        "Premium support"
      ],
      recommendedFor: "Perfect for funded startups, scale-ups, or businesses juggling multiple initiatives."
    },
    {
      name: "Embedded Team",
      price: "$13,800",
      bestFor: "Your full dev pod — like an in-house team, without the hiring",
      description: "You get a sprint-based team with strategic PM, devs, and optional QA/design. We own the roadmap and deliver like we're in-house.",
      features: [
        "Full-stack development team (2–3 devs)",
        "Strategic project management & roadmap execution",
        "Optional QA & design support",
        "Sprint-based, iterative delivery",
        "High-touch enterprise support"
      ],
      recommendedFor: "Ideal for complex platforms, in-house tool suites, or teams that need to ship fast and often."
    }
  ];

  const alternativeOptions = [
    {
      title: "Project-Based Development",
      description: "Need a specific feature or product built? Our project-based approach might be a better fit.",
      benefits: [
        "Fixed scope and timeline",
        "Clear deliverables",
        "One-time cost",
        "Perfect for MVPs or specific features"
      ]
    },
    {
      title: "Technical Consulting",
      description: "Get expert guidance on your technical strategy without committing to ongoing development.",
      benefits: [
        "Architecture planning",
        "Technology stack recommendations",
        "Security audits",
        "Best practices guidance"
      ]
    },
    {
      title: "Hybrid Approach",
      description: "Combine project work with flexible support hours for the best of both worlds.",
      benefits: [
        "Project-based development for major features",
        "Maintenance hours for ongoing support",
        "Scalable as needed",
        "Cost-effective for varying needs"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20" />
        </div>
        <div className="relative container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-5xl font-bold mb-6">Development as a Service</h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Transform your business with our flexible, scalable development services.
              Get a dedicated team of experts without the overhead.
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">Why Choose DaaS?</h2>
            <p className="text-lg text-gray-600">
              Our Development as a Service model provides the flexibility and expertise
              you need to accelerate your digital transformation.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="mb-6">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Server className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">Key Benefits</h2>
            <p className="text-lg text-gray-600">
              Experience the advantages of our Development as a Service model
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all"
              >
                <h3 className="text-xl font-semibold mb-6">{benefit.title}</h3>
                <ul className="space-y-4">
                  {benefit.points.map((point, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                      <span className="text-gray-600">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Cpu className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">Our Process</h2>
            <p className="text-lg text-gray-600">
              A streamlined approach to delivering exceptional results
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all border border-gray-100">
                  <div className="mb-6">{step.icon}</div>
                  <h3 className="text-xl font-semibold mb-4">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-yellow-400" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <DollarSign className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">Built for clarity. Priced for progress.</h2>
            <p className="text-lg text-gray-600">
              No surprise invoices. Just steady, reliable development.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {pricingPlans.map((plan, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all flex flex-col"
              >
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold mb-4">{plan.name}</h3>
                  <div className="text-sm text-gray-600 mb-2">Starting at</div>
                  <div className="text-3xl font-bold mb-2">{plan.price} <span className="text-gray-600">/mo</span></div>
                  <p className="text-gray-600 font-medium">{plan.bestFor}</p>
                </div>
                <p className="text-gray-600 mb-6">{plan.description}</p>
                <ul className="space-y-4 mb-8 flex-grow">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                {plan.recommendedFor && (
                  <p className="text-sm text-gray-600 italic mb-4 text-center">{plan.recommendedFor}</p>
                )}
                <button
                  onClick={() => handleGetStarted(plan.name)}
                  className="block w-full py-3 px-6 text-center bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-colors mt-auto"
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Alternative Options Section */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Briefcase className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
              <h2 className="text-3xl font-bold mb-6">Exploring Your Options</h2>
              <p className="text-lg text-gray-600">
                While DaaS offers many benefits, we understand it's not the right fit for every situation.
                Here are alternative ways we can support your development needs.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {alternativeOptions.map((option, index) => (
                <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                  <h3 className="text-xl font-bold mb-4">{option.title}</h3>
                  <p className="text-gray-600 mb-6">{option.description}</p>
                  <ul className="space-y-3">
                    {option.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                        <span className="text-gray-600">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="mt-12 text-center">
              <a
                href="/projects"
                className="inline-flex items-center px-6 py-3 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-all group"
              >
                View Project-Based Options
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Get Started?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's discuss how our DaaS model can accelerate your development projects.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-4 bg-yellow-400 text-slate-900 rounded-lg font-semibold hover:bg-yellow-300 transition-all group"
            >
              Schedule a Consultation
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      {/* Contact Modal */}
      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedPlan={selectedPlan}
      />
    </div>
  );
};

export default DaaS;